import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff, deduplicateRequest } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.users_api.user_permissions;

// Helper function to configure the Axios instance
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Wrap with cache
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'users_api',
  group: 'user_permissions',
  defaultTtl: '1m',
  hashLargeSegments: true,
  emitMeta: true,
});

// Fetch user permissions by local ID
export const getUserPermissionsByLocalId = async (localId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetAllUserPermissionsByLocalId/${localId}`, undefined, { ttl: '2m' })
    );
}

// Fetch user permissions by user ID
export const getUserPermissions = async (userId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetUserPermissions/${userId}`, undefined, { ttl: '2m' })
    );
}

// Update a user's permission
export const updateUserPermission = async (userId: number, permissionId: number, enabled: boolean) => {
    const res = await retryWithBackoff(() =>
        api.put('/UpdateUserPermission', {
            user_id: userId,
            permission_id: permissionId,
            enabled: enabled
        })
    );
    await cachedApi.clear();
    return res;
}

/**
 * Get user permission state - returns whether the user's permissions changed since last client sync.
 * Uses standard cache infrastructure with 1 minute TTL.
 * Also deduplicates concurrent requests to prevent multiple simultaneous calls.
 */
export const getUserPermissionState = async (userId: number) => {
    return await deduplicateRequest(
        `getUserPermissionState:${userId}`,
        () => retryWithBackoff(() =>
            cachedApi.get(`/GetUserPermissionState/${userId}`, undefined, { ttl: '1m' })
        )
    );
}

/**
 * Enable all permissions of specified types for a user
 * @param userId - The user ID
 * @param permissionTypes - Array of permission types to enable ('read', 'create', 'update', 'delete')
 */
export const enablePermissionsByTypes = async (userId: number, permissionTypes: string[]) => {
    const res = await retryWithBackoff(() =>
        api.put('/EnablePermissionsByTypes', {
            user_id: userId,
            permission_types: permissionTypes
        })
    );
    await cachedApi.clear();
    return res;
}

/**
 * Disable all permissions of specified types for a user
 * @param userId - The user ID
 * @param permissionTypes - Array of permission types to disable ('read', 'create', 'update', 'delete')
 */
export const disablePermissionsByTypes = async (userId: number, permissionTypes: string[]) => {
    const res = await retryWithBackoff(() =>
        api.put('/DisablePermissionsByTypes', {
            user_id: userId,
            permission_types: permissionTypes
        })
    );
    await cachedApi.clear();
    return res;
}